import * as React from 'react';
import { useState } from 'react';
import {
    List,
    DataTable,
    EmailField,
    DateField,
    SelectInput,
    TextInput,
    TopToolbar,
    ExportButton,
    ColumnsButton,
    useDefaultTitle,
    useListContext,
    FilterButton,
    useRecordContext,
    useNotify,
    useRefresh,
} from 'react-admin';
import { useMediaQuery, Theme, Chip, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, CircularProgress } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { getAccessToken } from '../authProvider';
import { API_BASE_URL } from '../config';
import {
    ROLE_CHOICES,
    STATUS_CHOICES,
    GENDER_CHOICES,
    VIP_CHOICES,
    TRIAL_CHOICES,
    SOURCE_CHOICES,
    ROLE_MAP,
    STATUS_MAP,
    GENDER_MAP,
    SOURCE_MAP,
} from './constants';

export type UserListConfig = {
    resource?: string;
    showRoleFilter?: boolean;
    showImport?: boolean;
    mode?: 'default' | 'player' | 'club' | 'coach' | 'admin';
};

type ChipColor = 'default' | 'primary' | 'secondary' | 'info' | 'success' | 'warning';

const UNKNOWN_LABEL = 'Unknown';
const UNKNOWN_CHIP: { label: string; color: ChipColor } = { label: UNKNOWN_LABEL, color: 'default' };

const GENDER_COLOR_MAP: Record<number, ChipColor> = {
    0: 'default',
    1: 'info',
    2: 'secondary',
};

const SOURCE_COLOR_MAP: Record<number, ChipColor> = {
    1: 'primary',
    2: 'success',
    3: 'warning',
    4: 'secondary',
    5: 'info',
};

const buildUserFilters = (showRoleFilter = true, mode: UserListConfig['mode'] = 'default') => {
    const showVipTrialFilters = mode === 'player' || mode === 'default';
    const filters: React.ReactElement[] = [
        <TextInput key="name" label="Name" source="name" />,
        <TextInput key="email" label="Email" source="email" />,
        <TextInput key="phone" label="Phone" source="phone" />,
        <SelectInput key="status" label="Status" source="status" choices={STATUS_CHOICES} />,
        <SelectInput key="gender" label="Gender" source="gender" choices={GENDER_CHOICES} />,
        <SelectInput key="sourceId" label="Source" source="sourceId" choices={SOURCE_CHOICES} />,
    ];

    if (mode !== 'player') {
        filters.push(<TextInput key="objectId" label="Object ID" source="objectId" />);
    }

    if (showVipTrialFilters) {
        filters.splice(5, 0, <SelectInput key="vip" label="VIP" source="vip" choices={VIP_CHOICES} />);
        filters.splice(6, 0, <SelectInput key="trial" label="Trial" source="trial" choices={TRIAL_CHOICES} />);
    }

    if (showRoleFilter) {
        filters.splice(3, 0, <SelectInput key="role" label="Role" source="role" choices={ROLE_CHOICES} />);
    }

    return filters;
};

const ImportExcelButton = () => {
    const notify = useNotify();
    const refresh = useRefresh();
    const fileInputRef = React.useRef<HTMLInputElement | null>(null);
    const [uploading, setUploading] = useState(false);

    const handleOpenFileDialog = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            setUploading(true);
            const token = await getAccessToken();
            if (!token) {
                notify('No access token available', { type: 'error' });
                return;
            }

            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${API_BASE_URL}/api/admin/users/upload`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formData,
            });

            let result: any = null;
            try {
                result = await response.json();
            } catch {
                // ignore non-json response
            }

            if (!response.ok || (result?.code && result.code !== 200)) {
                notify(result?.message || 'Failed to import users from Excel', { type: 'error' });
                return;
            }

            notify(result?.message || 'Users imported successfully', { type: 'success' });
            refresh();
        } catch {
            notify('Error importing users from Excel', { type: 'error' });
        } finally {
            setUploading(false);
            event.target.value = '';
        }
    };

    return (
        <>
            <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />
            <Button
                onClick={handleOpenFileDialog}
                disabled={uploading}
                size="small"
                startIcon={uploading ? <CircularProgress size={16} /> : <UploadFileIcon />}
            >
                Import Users
            </Button>
        </>
    );
};

const UserListActions = ({ showImport = true }: { showImport?: boolean }) => (
    <TopToolbar>
        <FilterButton />
        {showImport ? <ImportExcelButton /> : null}
        <ColumnsButton />
        {isSuperAdmin() && (
            <ExportButton />
        )}        
    </TopToolbar>
);

const UserTitle = () => {
    const title = useDefaultTitle();
    const { defaultTitle } = useListContext();
    return (
        <>
            <title>{`${title} - ${defaultTitle}`}</title>
            <span>{defaultTitle}</span>
        </>
    );
};

const RoleField = () => {
    const record = useRecordContext();
    if (!record) return null;
    const role = ROLE_MAP[record.role] || { label: 'Unknown', color: 'default' };
    return <Chip label={role.label} color={role.color as any} size="small" />;
};

const StatusField = () => {
    const record = useRecordContext();
    if (!record) return null;
    const status = STATUS_MAP[record.status] || { label: 'Unknown', color: 'default' };
    return <Chip label={status.label} color={status.color as any} size="small" />;
};

const GenderField = () => {
    const record = useRecordContext();
    if (!record) return null;
    const gender = Number(record.gender);
    return <Chip label={GENDER_MAP[gender] || UNKNOWN_LABEL} color={GENDER_COLOR_MAP[gender] || 'default'} size="small" />;
};

const VipField = () => {
    const record = useRecordContext();
    if (!record) return null;
    if (record.vip === 2) {
        return <Chip label="PREMIUM" color="secondary" size="small" />;
    }
    return record.vip === 1 ? (
        <Chip label="VIP" color="success" size="small" />
    ) : (
        <Chip label="FREE" color="default" size="small" />
    );
};

const TrialField = () => {
    const record = useRecordContext();
    if (!record) return null;
    return record.trial && Number(record.trial) > 0 ? (
        <Chip label="Trial" color="info" size="small" />
    ) : (
        <Chip label="No" color="default" size="small" />
    );
};

const SourceField = () => {
    const record = useRecordContext();
    if (!record) return null;
    const sourceId = Number(record.sourceId);
    return <Chip label={SOURCE_MAP[sourceId] || UNKNOWN_LABEL} color={SOURCE_COLOR_MAP[sourceId] || 'default'} size="small" />;
};

const PREFERRED_FOOT_MAP: Record<number, { label: string; color: ChipColor }> = {
    1: { label: 'Right', color: 'primary' },
    2: { label: 'Left', color: 'secondary' },
    3: { label: 'Both', color: 'success' },
};

const PreferredFootField = () => {
    const record = useRecordContext();
    if (!record) return null;
    const foot = PREFERRED_FOOT_MAP[Number(record.preferredFoot)] || UNKNOWN_CHIP;
    return <Chip label={foot.label} color={foot.color} size="small" />;
};

const HIGHLIGHT_MAP: Record<number, { label: string; color: ChipColor }> = {
    0: { label: 'Skipped', color: 'default' },
    1: { label: 'Not Uploaded', color: 'warning' },
    9: { label: 'Uploaded', color: 'success' },
};

const HighlightField = () => {
    const record = useRecordContext();
    if (!record) return null;
    const highlight = HIGHLIGHT_MAP[Number(record.hightLight)] || UNKNOWN_CHIP;
    return <Chip label={highlight.label} color={highlight.color} size="small" />;
};

type User = {
    id: number;
    email: string;
    name: string;
    role: number;
    status: number;
    gender: number;
    phone: string;
    location: string;
    vip: number;
    trial: number;
    sourceId: number;
    createTime: string;
    updateTime: string;
};

const Column = DataTable.Col<User>;

const isSuperAdmin = (): boolean => {
    const isSuperAdminStr = localStorage.getItem('isSuperAdmin');
    return isSuperAdminStr === 'true';
};

const ResetButton = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const refresh = useRefresh();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    if (!record) return null; 

    const handleOpen = (e: React.MouseEvent) => {
        e.stopPropagation();
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleConfirm = async () => {
        setLoading(true);
        try {
            const token = await getAccessToken();
            const response = await fetch(`${API_BASE_URL}/api/admin/reset-user/${record.id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            const result = await response.json();
            if (result.code === 200) {
                notify('User reset successfully', { type: 'success' });
                refresh();
            } else {
                notify(result.message || 'Failed to reset user', { type: 'error' });
            }
        } catch (error) {
            notify('Error resetting user', { type: 'error' });
        } finally {
            setLoading(false);
            setOpen(false);
        }
    };

    return (
        <>
            <Button
                size="small"
                color="warning"
                variant="outlined"
                onClick={handleOpen}
            >
                Reset
            </Button>
            <Dialog open={open} onClose={handleClose} onClick={e => e.stopPropagation()}>
                <DialogTitle>Reset User</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to reset user <strong>{record.name || record.email}</strong>? This will reset all user attributes to their default values.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={loading}>Cancel</Button>
                    <Button onClick={handleConfirm} color="warning" variant="contained" disabled={loading}>
                        {loading ? <CircularProgress size={20} /> : 'Confirm Reset'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export const createUserList = ({
    resource = 'admin/users',
    showRoleFilter = true,
    showImport = true,
    mode = 'default',
}: UserListConfig = {}) => {
    const UserList = () => {
        const isSmall = useMediaQuery<Theme>(theme => theme.breakpoints.down('md'));
        const isPlayerMode = mode === 'player';      

        return (
            <List
                resource={resource}
                filters={buildUserFilters(showRoleFilter, mode)}
                sort={{ field: 'createTime', order: 'DESC' }}
                perPage={25}
                actions={<UserListActions showImport={showImport} />}
                title={<UserTitle />}
            >
                <DataTable
                    rowClick="edit"
                    bulkActionButtons={false}
                    sx={{
                        '& .column-phone': {
                            display: isSmall ? 'none' : 'table-cell',
                        },
                        '& .column-location': {
                            display: isSmall ? 'none' : 'table-cell',
                        },
                        '& .column-vip': {
                            display: isSmall ? 'none' : 'table-cell',
                        },
                        '& .column-trial': {
                            display: isSmall ? 'none' : 'table-cell',
                        },
                        '& .column-sourceId': {
                            display: isSmall ? 'none' : 'table-cell',
                        },
                    }}
                >
                    <Column source="name" label="Name" />
                    <Column source="email" label="Email">
                        <EmailField source="email" />
                    </Column>
                    <Column source="role" label="Role">
                        <RoleField />
                    </Column>
                    <Column source="status" label="Status">
                        <StatusField />
                    </Column>
                    <Column source="gender" label="Gender">
                        <GenderField />
                    </Column>
                    <Column source="birthDate" label="Birth Date">
                        <DateField source="birthDate" showTime={false} />
                    </Column>
                    <Column source="phone" label="Phone" />
                    <Column source="location" label="Location" />
                    {isPlayerMode && (
                        <Column source="vip" label="VIP">
                            <VipField />
                        </Column>
                    )}
                    {isPlayerMode && (
                        <Column source="trial" label="Trial">
                            <TrialField />
                        </Column>
                    )}
                    {isPlayerMode && (
                        <Column source="preferredFoot" label="Preferred Foot">
                            <PreferredFootField />
                        </Column>
                    )}
                    {isPlayerMode && <Column source="preferredPositions" label="Preferred Positions" />}
                    {isPlayerMode && <Column source="heightUnit" label="Height Unit" />}
                    {isPlayerMode && <Column source="height" label="Height" />}
                    {isPlayerMode && <Column source="weightUnit" label="Weight Unit" />}
                    {isPlayerMode && <Column source="weight" label="Weight" />}
                    {isPlayerMode && (
                        <Column source="hightLight" label="Highlight">
                            <HighlightField />
                        </Column>
                    )}
                    {mode === 'club' && <Column source="clubName" label="Club Name" />}
                    {mode === 'club' && <Column source="clubAddress" label="Club Address" />}
                    {mode === 'club' && <Column source="clubPicUrl" label="Club PicUrl" />}
                    {mode === 'default' && <Column source="objectId" label="Object ID" />}
                    <Column source="sourceId" label="Source">
                        <SourceField />
                    </Column>
                    <Column source="createTime" label="Created">
                        <DateField source="createTime" showTime />
                    </Column>
                    <Column source="updateTime" label="Updated">
                        <DateField source="updateTime" showTime />
                    </Column>
                    {isSuperAdmin() && (
                        <Column source="id" label="Actions">
                            <ResetButton />
                        </Column>
                    )}
                </DataTable>
            </List>
        );
    };

    return UserList;
};

const UserList = createUserList();

export default UserList;
